import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ModelCommentEntity, ModelCommentThread } from '@view-model/models/sticky/ModelComment';
import { Markdown } from '@framework/ui/elements/Markdown';
import { useNavigate } from 'react-router';
import type { ViewEntity } from '@view-model/domain/view/ViewEntity';
import { generateDefaultUserIcon } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { formatRelativeTime } from '@framework/i18n/formatRelativeTime';
import { MODEL_COMMENT_RELATIVE_TIME_I18N_KEYS } from '@view-model/models/sticky/ModelComment/components/ModelCommentItem/modelCommentRelativeTimeI18nKeys';
import { Timestamp } from '@framework/Timestamp';

type Props = {
  commentThread: ModelCommentThread;
  view: ViewEntity;
  /** 返信コメントが検索にヒットした場合に指定する。未指定ならプライマリコメントを表示する。 */
  displayComment?: ModelCommentEntity;
};

export const CommentListItem: React.FC<Props> = ({ commentThread, view, displayComment }: Props) => {
  const { t } = useTranslation();
  const primaryComment = commentThread.getPrimaryComment();
  const shownComment = displayComment ?? primaryComment;
  const shownCommentAuthor = useUserPublicProfile(shownComment.userId);

  const navigate = useNavigate();

  const handleMoveToView = useCallback(
    (e?: React.MouseEvent<HTMLDivElement>) => {
      // Markdown 内のリンク・ボタン等インタラクティブ要素をクリックした場合は
      // 親要素のナビゲーションを実行しない
      const target = e?.target;
      if (target instanceof Element && target.closest('a, button')) return;

      const query = new URLSearchParams();

      // flushSync で同期レンダリングを強制し、targetCommentThread なしの中間状態を経由させる。
      // これにより、同一スレッドを再クリックした場合でも modelCommentThreadId が
      // null → threadId と変化し effect が再実行されてスレッドが開く。
      query.set('moveTime', Date.now().toString());
      flushSync(() => {
        navigate({ search: query.toString() }, { replace: true });
      });

      // 新形式 (targetCommentThread + targetModel) でナビゲートすることで、
      // 共有リンクと同様に RTDB からスレッド位置を取得してコメントを画面中央に表示する。
      // targetView も付与することで、RTDB 位置取得失敗時（未保存・削除済み等）でも
      // ビュー選択のフォールバックが機能するようにする。
      query.set('targetCommentThread', commentThread.id);
      query.set('targetModel', view.modelId);
      query.set('targetView', view.id);
      navigate({ search: query.toString() }, { replace: true });
    },
    [view, commentThread, navigate]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleMoveToView();
      }
    },
    [handleMoveToView]
  );

  // 最初のコメント投稿者情報が取得できるまでの間は非表示
  if (!shownCommentAuthor) return null;

  const replyCount = commentThread.getRegularReplyComments().length;
  const relativeTime = formatRelativeTime(
    shownComment.getPostAt(),
    Timestamp.now(),
    t,
    MODEL_COMMENT_RELATIVE_TIME_I18N_KEYS
  );

  return (
    // Markdown 本文にリンク等インタラクティブ要素が含まれ得るため button でなく div+role="button" を使用
    <div
      role="button"
      tabIndex={0}
      className="cursor-pointer px-4 py-3 hover:bg-gray-50"
      onClick={handleMoveToView}
      onKeyDown={handleKeyDown}
    >
      <div className="mb-1.5 text-xs text-gray-600">{relativeTime}</div>
      <div className="flex items-center gap-2">
        <div className="size-6 shrink-0 overflow-hidden rounded-full">
          {shownCommentAuthor.imageUrl ? (
            <img
              src={shownCommentAuthor.imageUrl}
              alt={shownCommentAuthor.name}
              className="size-full bg-gray-400 object-cover"
            />
          ) : (
            <div className="size-full bg-gray-400">
              {generateDefaultUserIcon({
                id: shownCommentAuthor.id,
                name: shownCommentAuthor.name,
                isAnonymous: shownCommentAuthor.isAnonymous,
                size: 24,
              })}
            </div>
          )}
        </div>
        <span className="flex-1 truncate text-sm font-bold">{shownCommentAuthor.name}</span>
        {commentThread.isResolved() && (
          <>
            <FontAwesomeIcon icon={faCheck} aria-hidden className="shrink-0 text-sm text-brand" />
            <span className="sr-only">{t('viewModel.commentList.filterResolved')}</span>
          </>
        )}
      </div>
      <div className="mt-1.5 line-clamp-3 break-all text-sm text-gray-800">
        <Markdown className="markdown-body" source={shownComment.getBody()} />
      </div>
      {replyCount > 0 && (
        <div className="mt-1 text-xs text-blue-600">{t('canvas.comment.reply.count', { count: replyCount })}</div>
      )}
    </div>
  );
};
